import React, { useEffect, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTransition, animated } from 'react-spring';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from 'routes';
import { usePlannerConfig } from 'contexts/plannerConfig';
import TapIcon from './tap.svg?react';
import { reloadPlanner } from 'redirects';
import { getQuery } from 'utils/queryUtils';
import { setShowBannerMessage, setShowScreensaver, shouldShowScreensaver } from 'ducks/layout';
import { resetSheets } from 'ducks/sheets';
import { useAppDispatch } from 'ducks/hooks';
import {
  StoreIdCookieInformationService,
  ZipCodeCookieInformationService
} from '@inter-ikea-kompis/services';
import { useSelector } from 'react-redux';
import { useServiceSettings } from 'contexts/serviceSettings';

const Wrapper = styled(animated.div)`
  z-index: 2000;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;

  > video {
    width: 100%;
    height: 100%;
  }
`;

const pulse = keyframes`
  33% {
    transform: scale(1);
  }

  66% {
    transform: scale(0.95);
  }

  100% {
    transform: scale(1);
  }
`;

const Tap = styled(TapIcon)`
  position: absolute;
  right: 10%;
  top: 60%;
  z-index: 2001;
  transform: scale(1.3);
  pointer-events: none;

  > * .tap-icon-animation {
    transform-origin: center center;
    animation: ${pulse} 1.6s ease-in-out infinite;
  }
`;

const DEFAULT_SCREEN_SAVER_TIMEOUT = 360000; // 6min
const DEFAULT_PLANNER_RESET_TIMEOUT = 600000; // 10min

const Screensaver = () => {
  const {
    plannerConfig: { screensaverPath, secondaryScreensaverPath }
  } = usePlannerConfig();

  const [videoBlobUrl, setVideoBlobUrl] = useState<string>();
  const [timer, setTimer] = useState(-1);
  const [restartTimer, setRestartTimer] = useState(-1);
  const [resetPlanner, setResetPlanner] = useState(false);
  const showScreensaver = useSelector(shouldShowScreensaver);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { dexfSettings } = useServiceSettings();
  const screensaver =
    dexfSettings.general?.screensaverUseAltVideo && secondaryScreensaverPath
      ? secondaryScreensaverPath
      : screensaverPath;

  useEffect(() => {
    screensaver &&
      fetch(screensaver)
        .then(response => response.blob())
        .then(blob => {
          setVideoBlobUrl(window.URL.createObjectURL(blob));
        });
  }, [screensaver]);

  const screensaverTimeoutOverride = getQuery().get('screensaverTimeout');
  const screensaverTimeout =
    screensaverTimeoutOverride !== null
      ? Number.parseInt(screensaverTimeoutOverride)
      : DEFAULT_SCREEN_SAVER_TIMEOUT;

  const screensaverResetTimeout =
    screensaverTimeoutOverride !== null
      ? Number.parseInt(screensaverTimeoutOverride)
      : DEFAULT_PLANNER_RESET_TIMEOUT;

  const resetScreensaver = useCallback(() => {
    const t = window?.setTimeout(() => {
      dispatch(setShowScreensaver(true));
      dispatch(resetSheets());
    }, screensaverTimeout);
    setTimer(t);
    dispatch(setShowScreensaver(false));
  }, [dispatch, screensaverTimeout]);

  const restartSession = useCallback(() => {
    if (showScreensaver) {
      const t = window?.setTimeout(() => {
        setResetPlanner(true);
      }, screensaverResetTimeout);
      setRestartTimer(t);
      dexfSettings?.general?.bannerMessageEnabledKiosk !== undefined &&
        dispatch(setShowBannerMessage(dexfSettings.general.bannerMessageEnabledKiosk));
    }
  }, [
    showScreensaver,
    screensaverResetTimeout,
    dexfSettings.general.bannerMessageEnabledKiosk,
    dispatch
  ]);

  useEffect(() => {
    resetScreensaver();
  }, [resetScreensaver]);

  useEffect(() => {
    restartSession();
  }, [restartSession]);

  // Any click thats not on the screensaver should reset the screensaver
  useEffect(() => {
    const onInteraction = (e: any) => {
      if (e.target.id !== 'screensaver') {
        clearTimeout(timer);
        resetScreensaver();
      }
      if (e.target.id === 'screensaver') {
        if (resetPlanner) {
          const storeId = getQuery().get('storeId');
          if (storeId) {
            const storeService = new StoreIdCookieInformationService({ settings: dexfSettings });
            storeService.setStoreId(storeId);
          }
          const zipService = new ZipCodeCookieInformationService({ settings: dexfSettings });
          zipService.clearZipCode();
          history.push(AppRoutes.LANDING_PAGE);
          reloadPlanner();
        } else {
          clearTimeout(restartTimer);
          dispatch(resetSheets());
          history.push(AppRoutes.HOME);
        }

        dispatch(setShowScreensaver(false));
      }
    };
    document.addEventListener('keypress', onInteraction, true);
    document.addEventListener('mousedown', onInteraction, true);
    document.addEventListener('touchstart', onInteraction, true);
    return () => {
      document.removeEventListener('keypress', onInteraction, true);
      document.removeEventListener('mousedown', onInteraction, true);
      document.removeEventListener('touchstart', onInteraction, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, history, resetPlanner, resetScreensaver, restartTimer, timer]);

  useEffect(() => {
    if (showScreensaver) {
      if (history.location.pathname === AppRoutes.PLANNER) {
        history.push(AppRoutes.PLANNER_SCREENSAVER);
      }
    }
  }, [showScreensaver, history]);

  const transitions = useTransition(showScreensaver, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 }
  });

  return (
    <>
      {transitions(
        (style, item, { key }) =>
          screensaverPath &&
          item && (
            <Wrapper test-id="screensaver" key={key} style={style} data-testid="screensaver">
              {showScreensaver && (
                <video src={videoBlobUrl} id="screensaver" loop autoPlay muted></video>
              )}
              <Tap />
            </Wrapper>
          )
      )}
    </>
  );
};

export default Screensaver;
